import type { BuildingCategory, BuildingInStoreInfo, FloorInfo } from '@/services/dto';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useConfigStore } from './configStore';
import type { TFunction } from 'i18next';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import type { FnbActions, FnbFilterConfig, FnbState } from '@/stores/types/fnbStore';
import fnbUtils from '@/utils/fnb';
import poiUtils from '@/utils/poi';
import type { PoiContentSlide } from '@/types/shop';

interface FnbStore extends FnbState, FnbActions { }

export const useFnbStore = create<FnbStore>()(
    devtools(
        (set, get) => ({
            // Initial state
            rawFnbContents: [],
            fnbContents: [],
            searchText: '',
            selectedFloor: {
                id: 'all',
                label: '',
                value: '',
                data: null,
            },
            selectedBuildingId: 'all',
            selectedCategory: null,
            t: null,
            currentMode: SHOP_SEARCH_MODE.SEARCH_BY_TEXT,
            keyboardLanguage: 'ko',

            setKeyboardLanguage: (language: 'ko' | 'en') => {
                const prev = get().keyboardLanguage;
                if (prev === language) return;

                set({ keyboardLanguage: language });
            },

            setCurrentMode: (mode: SHOP_SEARCH_MODE) => {
                const prev = get().currentMode;
                if (prev === mode) return;

                set({ currentMode: mode });
            },

            setSearchText: (text: string) => {
                const prev = get().searchText;
                if (prev === text) return;

                set({ searchText: text });
            },

            setSelectedFloor: (floor: { id: string; label: string; value: string; data: FloorInfo | null } | null) => {
                const prev = get().selectedFloor;
                if (prev?.id === floor?.id) return;

                set({ selectedFloor: floor });
            },

            setSelectedBuildingId: (buildingId: string | null) => {
                const prev = get().selectedBuildingId;
                if (prev === buildingId) return;

                set({ selectedBuildingId: buildingId });
            },

            setSelectedCategory: (category: BuildingCategory) => {
                const prev = get().selectedCategory;
                if (prev === category) return;

                set({ selectedCategory: category });
            },

            setTranslationFunction: (t: TFunction<'translation', undefined>) => {
                const prev = get().t;
                if (prev === t) return;

                set({ t });
            },

            resetFilters: () => {
                const state = get();
                const { t } = state;
                const { config } = useConfigStore.getState();
                const {
                    mergedCategories: { fnb },
                    server,
                } = config;

                const allFloorText = t ? t('fnb.search.text.content.all_floor') : 'All Floors';
                const isMultipleBuildings = server?.store?.buildings && server?.store?.buildings?.length > 1;

                // Reset all filters to initial state
                set({
                    searchText: '',
                    selectedFloor: {
                        id: 'all',
                        label: allFloorText,
                        value: 'all',
                        data: null,
                    },
                    selectedBuildingId: isMultipleBuildings ? 'all' : (server?.store?.buildings?.[0]?.id ?? 'all'),
                    selectedCategory: isMultipleBuildings ? fnb?.[0] : server?.store?.buildings?.[0]?.filterCategories?.fnb?.[0],
                });
            },

            updateFnbContents: (searchMode: SHOP_SEARCH_MODE): PoiContentSlide[] => {
                const state = get();
                const { selectedFloor, selectedBuildingId, selectedCategory, searchText, keyboardLanguage } = state;
                const { config } = useConfigStore.getState();
                const {
                    ui: { language },
                } = config;

                // Regenerate rawFnbContents with searchText and keyboardLanguage to include matching keywords
                const rawFnbContents = fnbUtils.convertPoiContentToSlide(language, config.server?.store?.buildings ?? [], searchText);

                // Find storeCode and buildingCode from selectedBuildingId
                let storeCode: string | null = null;
                let buildingCode: string | null = null;

                if (selectedBuildingId && selectedBuildingId !== 'all') {
                    const selectedBuilding = config.server?.store?.buildings?.find((b) => b.id === selectedBuildingId);
                    if (selectedBuilding) {
                        storeCode = selectedBuilding.storeCode;
                        buildingCode = selectedBuilding.buildingCode;
                    }
                }

                const filterConfig: FnbFilterConfig = {
                    language,
                    selectedBuildingId,
                    storeCode,
                    buildingCode,
                    selectedFloor,
                    selectedCategory,
                    searchText,
                    buildings: config.server?.store?.buildings,
                };

                const fnbFilter = fnbUtils.createFnbFilter(searchMode, filterConfig, keyboardLanguage);
                const filteredContents = rawFnbContents?.filter(fnbFilter);

                const sortedContents = poiUtils.getSortedContents([...filteredContents], language);

                set({ fnbContents: sortedContents });

                return sortedContents;
            },

            initialize: (t: TFunction<'translation', undefined>) => {
                const { config } = useConfigStore.getState();
                const {
                    ui: { language },
                    mergedCategories: { fnb },
                    server,
                } = config;

                const rawContents = fnbUtils.convertPoiContentToSlide(language, config.server?.store?.buildings ?? []);
                const isMultipleBuildings = server?.store?.buildings && server?.store?.buildings?.length > 1;
                const selectedBuildingId = isMultipleBuildings ? 'all' : (server?.store?.buildings?.[0]?.id ?? 'all');
                const selectedCategory = isMultipleBuildings ? fnb?.[0] : server?.store?.buildings?.[0]?.filterCategories?.fnb?.[0];

                set({ t, rawFnbContents: rawContents, currentMode: SHOP_SEARCH_MODE.SEARCH_BY_TEXT, selectedBuildingId, selectedCategory });
            },
        }),
        {
            name: 'fnb-store',
        }
    )
);

let previousBuildings: BuildingInStoreInfo[] | undefined = undefined;
let previousLanguage: string = '';

useConfigStore.subscribe(() => {
    const { config } = useConfigStore.getState();
    const currentBuildings = config.server?.store?.buildings;
    const currentLanguage = config.ui?.language;

    if (currentBuildings !== previousBuildings || currentLanguage !== previousLanguage) {
        previousBuildings = currentBuildings;
        previousLanguage = currentLanguage;

        const { initialize, t } = useFnbStore.getState();
        if (t) {
            initialize(t);
        }
    }
});
